import { Injectable, inject } from '@angular/core';

import { BehaviorSubject, Observable, map, tap, catchError, throwError } from 'rxjs';

import { User } from '../../../models/user.model';
import { endpoints } from '../../../../environments/endpoints';
import { LaravelDefaultResponse } from '../../../interfaces/responses';
import { ApiService } from '../../shared/services/api.service';
import { AuthEventService } from '../../auth/services/auth-event.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _userSubject = new BehaviorSubject<User | null>(null);
  private _apiService = inject(ApiService);
  private _authEventService = inject(AuthEventService);

  constructor() {
    // Listen for force logout events from the token interceptor
    this._authEventService.forceLogout$.subscribe(() => {
      this._userSubject.next(null);
    });
  }

  public fetchUser(): Observable<User> {
    return this._apiService.get<{ data: User }>(endpoints.user, {}, { headers: this._apiService.validationOnlyHeader }).pipe(
      map((resp: { data: User }) => {
        const user = resp.data;
        this._userSubject.next(new User(user));
        return user;
      }),
      catchError((error) => {
        // If fetching user fails, the token is probably invalid
        console.error('Failed to fetch user, clearing token:', error);
        localStorage.removeItem('token');
        this._userSubject.next(null);
        return throwError(() => error);
      })
    );
  }

  public logout(): Observable<LaravelDefaultResponse<boolean>> {
    return this._apiService.get<LaravelDefaultResponse<boolean>>(endpoints.logout).pipe(
      tap(() => {
        this._userSubject.next(null);
        localStorage.removeItem('token');
      })
    );
  }

  public isLoggedIn(): boolean {
    // Check both token existence and user object
    const token = localStorage.getItem('token');
    const user = this.getUser().value;
    return token !== null && user !== null;
  }

  public hasToken(): boolean {
    return localStorage.getItem('token') !== null;
  }

  public getUser(): BehaviorSubject<User | null> {
    return this._userSubject;
  }
}
